import React, { useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CompareTamponsBody from '@components/dashboard/modals/CompareTamponsBody';
import TamponBoxConfig from '@components/dashboard/TamponBoxConfig';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import Modal from '@components/shared/Modal';
import { CompareModalRow } from '@components/tampons/SubscribeCompareModal';
import SubscribeOptionTamponCount from '@components/tampons/SubscribeOptionTamponCount';
import { BoxState, default12TamponConfiguration, default18TamponConfiguration, TamponType } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import IconArrow from '@images/arrow.svg';
import Drops2Cbd from '@images/drops2-cbd.svg';
import Drops2 from '@images/drops2.svg';
import Drops3Cbd from '@images/drops3-cbd.svg';
import Drops3 from '@images/drops3.svg';
import SelectedRadio from '@images/selected-radio.svg';
import QuestionImage from '@images/storefront/question-gold-bg.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { replaceText } from '@utils/handleTextReplacement';
import { useScrollBlock } from '@utils/useScrollBlock';

interface ModalProps {
  isOpen: boolean;
  closeModal: () => void;
  boxState: BoxState;
  setBoxStateOuter: (value: BoxState) => void;
  setBoxState: (value: BoxState) => void;
  preset?: boolean;
}

const presetBoxes: BoxState[] = [
  {
    boxSize: 18,
    cbdsuper: 9,
    cbdregular: 9,
    nakedregular: 0,
    nakedsuper: 0
  },
  {
    boxSize: 18,
    cbdsuper: 18,
    cbdregular: 0,
    nakedregular: 0,
    nakedsuper: 0
  },
  {
    boxSize: 12,
    cbdsuper: 6,
    cbdregular: 6,
    nakedregular: 0,
    nakedsuper: 0
  },
  {
    boxSize: 18,
    cbdsuper: 0,
    cbdregular: 0,
    nakedsuper: 9,
    nakedregular: 9
  }
];

const TamponSelectionModal: React.FC<ModalProps> = ({ isOpen, closeModal, boxState, setBoxState, setBoxStateOuter, preset = false }) => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const [expandCompare, setExpandCompare] = useState(false);
  const [modalBoxState, setModalBoxState] = useState<BoxState>(boxState);
  const { boxSize, nakedsuper, nakedregular, cbdregular, cbdsuper } = modalBoxState;
  const sumOfTamps = nakedregular + nakedsuper + cbdregular + cbdsuper;
  const [isFull, setIsFull] = useState(false);
  const modalTitle = preset ? t('components.choose-your-box').toUpperCase() : t('components.customise-your-box').toUpperCase();
  const [selectedBox, setSelectedBox] = useState(presetBoxes[0]);
  const [blockScroll, allowScroll] = useScrollBlock();
  const location = useLocation();
  const organicTamponsLandingURL = '/products/organic-tampons';
  const hideCBDWords = country === 'US' || !location || location?.pathname?.includes(organicTamponsLandingURL);
  const CBDText = hideCBDWords ? 'Coated' : 'CBD';

  useEffect(() => {
    if (isOpen) {
      blockScroll();
    } else {
      allowScroll();
    }
  }, [isOpen]);

  useEffect(() => {
    // sets the box state of the header
    if (sumOfTamps === boxSize) {
      setIsFull(true);
    } else setIsFull(false);
  }, [modalBoxState]);

  const updateSelectedBox = (item: BoxState) => {
    setSelectedBox(item);
    setModalBoxState(item);
  };

  // funtion to handle both addition and subtraction
  const handleCountAdd = (tampType: TamponType) => {
    setModalBoxState({
      ...modalBoxState,
      [tampType]: modalBoxState[tampType] + 1
    });
  };

  const handleCountRemove = (tampType: TamponType) => {
    setModalBoxState({
      ...modalBoxState,
      [tampType]: modalBoxState[tampType] - 1
    });
  };

  const items = [
    {
      text: t('components.highly-absorbent'),
      cbd: true,
      naked: true
    },
    {
      text: country === CountryCode.US ? 'No-shed sleeve' : t('components.prevent-fibre-loss'),
      cbd: true,
      naked: true
    },
    {
      text: country === CountryCode.US ? 'Cramp-soothing coating' : t('components.infused-with-cbd'),
      cbd: false,
      naked: true
    },
    {
      text: t('components.chemicals-used'),
      cbd: true,
      naked: true
    },
    {
      text: country === CountryCode.US ? 'Sanitised' : t('components.risk-of-tss'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.sugarcane-applicator'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.wrappers'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.fully-sustainable'),
      cbd: true,
      naked: true
    },
    {
      text: t('components.ethically'),
      cbd: true,
      naked: true
    }
  ];

  const children = (
    <ModalContainer>
      <CompareWrap onClick={() => setExpandCompare(!expandCompare)} expanded={expandCompare}>
        <CompareDropdownButton>
          <OpenCompare>
            <QuestionImage />
            <span>{t('components.compare')}</span>
          </OpenCompare>
          <IconArrowStyled expanded={expandCompare} />
        </CompareDropdownButton>
        <CompareTamponsBody country={country} />
        <CompareHeaderRow>
          <span>{t('components.compare-tampons-modal.detailed-comparison')}</span>
          <span>{t('components.naked')}</span>
          <span>{CBDText}</span>
        </CompareHeaderRow>
        {items.map((item: any, idx: number) => (
          <CompareModalRow key={idx} text={item.text} cbd={item.cbd} naked={item.naked} />
        ))}
      </CompareWrap>

      {preset ? (
        <PresetBoxWrap>
          {presetBoxes.map((item, index) => (
            <div onClick={() => updateSelectedBox(item)} key={index}>
              <TamponBoxConfig variants={item} selected={item === selectedBox} />
            </div>
          ))}
        </PresetBoxWrap>
      ) : (
        <>
          <Text>How many tampons would you like?</Text>
          <SizeSelectWrap>
            <SelectButton isSelected={boxSize === 12} onClick={() => setModalBoxState(default12TamponConfiguration)}>
              <TextWrap>{t('components.tampon-boxes.12-tampons')}</TextWrap>
            </SelectButton>
            <SelectButton isSelected={boxSize === 18} onClick={() => setModalBoxState(default18TamponConfiguration)}>
              <TextWrap>{t('components.tampon-boxes.18-tampons')}</TextWrap>
            </SelectButton>
          </SizeSelectWrap>
          <OptionWrap>
            <SubscribeOptionTamponCount
              title={replaceText('CBD', country, location)}
              subtitle={t('components.super')}
              count={cbdsuper || 0}
              onIncrease={() => handleCountAdd(TamponType.CBD_SUPER)}
              onDecrease={() => handleCountRemove(TamponType.CBD_SUPER)}
              canDecrease={cbdsuper > 0}
              canIncrease={!isFull}
              Image={Drops3Cbd}
            />
            <SubscribeOptionTamponCount
              title={replaceText('CBD', country, location)}
              subtitle={t('components.regular')}
              count={cbdregular || 0}
              onIncrease={() => handleCountAdd(TamponType.CBD_REGULAR)}
              onDecrease={() => handleCountRemove(TamponType.CBD_REGULAR)}
              canDecrease={cbdregular > 0}
              canIncrease={!isFull}
              Image={Drops2Cbd}
            />
            <SubscribeOptionTamponCount
              title={t('components.naked')}
              subtitle={t('components.super')}
              count={nakedsuper || 0}
              onIncrease={() => handleCountAdd(TamponType.NAKED_SUPER)}
              onDecrease={() => handleCountRemove(TamponType.NAKED_SUPER)}
              canDecrease={nakedsuper > 0}
              canIncrease={!isFull}
              Image={Drops3}
            />
            <SubscribeOptionTamponCount
              title={t('components.naked')}
              subtitle={t('components.regular')}
              count={nakedregular || 0}
              onIncrease={() => handleCountAdd(TamponType.NAKED_REGULAR)}
              onDecrease={() => handleCountRemove(TamponType.NAKED_REGULAR)}
              canDecrease={nakedregular > 0}
              canIncrease={!isFull}
              Image={Drops2}
            />
          </OptionWrap>
        </>
      )}
      <ConfirmButton>
        <Button
          disabled={sumOfTamps < boxSize}
          onClick={() => {
            setBoxState(modalBoxState);
            setBoxStateOuter(modalBoxState);
            closeModal();
          }}
        >
          {t('components.confirm-selection')}
        </Button>
      </ConfirmButton>
    </ModalContainer>
  );

  return (
    <Modal
      title={modalTitle}
      modalOpen={isOpen}
      onClose={() => {
        closeModal();
      }}
      stickyHeader={true}
      isWide
      paddingDesktop="0"
      paddingMobile="0"
    >
      {children}
    </Modal>
  );
};

export default TamponSelectionModal;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  justify-content: space-between;
  padding-inline: 30px;

  & > div {
    width: 48%;
    margin: 20px 0;
    flex-direction: column;

    .image-title-wrap {
      margin-bottom: 20px;

      & > div {
        display: flex;
        gap: 5px;
        margin-right: 0;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      margin: -2px 0 0 0;
      border-left-width: 0;
      border-right-width: 0;
      flex-direction: row;

      .image-title-wrap {
        margin-bottom: 0;
      }
    }
  }

  @media (max-width: 768px) {
    padding-inline: 0;
  }
`;

const TextWrap = styled.div`
  font-family: BrownStd;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-family: BrownStd;
    font-size: 13px;
    line-height: 20px;
  }
`;

interface CompareProps {
  expanded?: boolean;
}

const CompareDropdownButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${Colors.ecru};
  width: 100%;

  @media (max-width: 768px) {
    justify-content: space-between;
    padding-left: 20px;
    width: auto;
  }
`;

const OpenCompare = styled.div`
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 50px;

  span {
    text-transform: uppercase;
    margin-left: 10px;
    font-family: BrownStd;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-align: center;

    @media (max-width: 768px) {
      text-align: left;
    }
  }
`;

interface ArrowProps {
  expanded: boolean;
}

const IconArrowStyled = styled(IconArrow)<ArrowProps>`
  position: absolute;
  right: 40px;
  transform: ${(props) => props.expanded && 'rotate(180deg)'};

  @media (max-width: 768px) {
    right: 20px;
  }
`;

const CompareWrap = styled.button<CompareProps>`
  all: unset;
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  transition: height 0.3s ease-in-out;
  height: ${(props) => (props.expanded ? '100%' : '50px')};
  position: relative;
`;

const Text = styled.div`
  padding: 30px 0;
  font-family: BrownStd;
  font-size: 18px;
  font-weight: 400;
  line-height: 27px;
  text-align: center;
`;

const ConfirmButton = styled.div`
  display: flex;
  width: 100%;
  justify-content: end;
  background: ${Colors.ecru}80;
  border-top: 1px solid ${Colors.ecru};
  padding: 20px 30px;

  @media (max-width: 768px) {
    justify-content: center;

    & > button {
      width: 100%;
    }
  }
`;

interface SelectButtonProps {
  isSelected: boolean;
  value?: string | number;
  onClick?: () => void;
}

const SelectButton: React.FC<SelectButtonProps> = ({ children, isSelected, value, onClick }) => (
  <ButtonWrap $isSelected={isSelected} value={value} onClick={onClick}>
    {isSelected ? <SelectedRadio /> : <UnselectedRadio />}
    {children}
  </ButtonWrap>
);

const ButtonWrap = styled.button<{
  $isSelected: boolean;
}>`
  all: unset;
  cursor: pointer;
  padding: 20px 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.$isSelected ? `2px solid ${Colors.gold}` : `1px solid ${Colors.paleGold}`)};
  background: ${(props) => (props.$isSelected ? Colors.ecru : Colors.cream)};
  width: 40%;

  svg {
    margin-right: 10px;
  }

  @media (max-width: 768px) {
    width: 40%;
  }

  @media (max-width: 410px) {
    padding: 15px 20px;
    width: auto;
  }
`;

const SizeSelectWrap = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 20px;
    gap: 20px;
  }

  @media (max-width: 410px) {
    justify-content: space-between;
    gap: 0;
  }
`;

const PresetBoxWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 30px;
  gap: 20px;

  & > div {
    flex-basis: calc(33.33% - 20px);
    cursor: pointer;

    @media (max-width: 768px) {
      flex-basis: calc(50% - 20px);
    }

    @media (max-width: 660px) {
      flex-basis: 100%;
    }
  }
`;

const CompareHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 20px;
  border-bottom: 1px solid #faead6;

  span {
    display: flex;
    flex-direction: column;
    width: 70px;

    font-family: 'BrownStd';
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-align: center;
    text-transform: uppercase;
    color: ${Colors.darkGreen};

    &:first-child {
      margin-left: 0;
      flex: 1;
      text-align: left;
      width: auto;
    }

    &:last-child {
      padding-right: 5px;
      text-align: right;
    }
  }
`;
